/**
 * @file
 * Keep Authors details independently scrollable within the visible page area.
 * The CSS fallback remains ordinary, fully expanded page content.
 */
(function (Drupal, once) {
  'use strict';
  const selector = '.view-authors.view-display-id-page_1 .vertical-tabs';
  const cleanups = new WeakMap();

  function install(tabs) {
    const navigation = document.querySelector('.sticky-navigation');
    const controls = document.querySelector('.sticky-page-navigation');
    const column = tabs.querySelector('.authors-detail-column');
    const panel = tabs.querySelector('.vertical-tabs__panes');
    const footer = tabs.closest('.layout-container')?.querySelector('.region-footer');
    if (!navigation || !column || !panel || !footer || !window.ResizeObserver) return;

    // Matches --tablet in variables.css and authors-mobile-menu.js.
    const mobile = window.matchMedia('(width < 1024px)');
    const originalEdges = tabs.dataset.footerEdges;
    const originalSpacedEdges = tabs.dataset.footerSpacedEdges;
    let frame = null;
    let active = null;
    let disposed = false;
    let anchorFrame = null;

    function revealAnchor() {
      cancelAnimationFrame(anchorFrame);
      // Allow tab activation, accordion placement and native fragment scrolling
      // to finish before measuring the name against the actual sticky edge.
      anchorFrame = requestAnimationFrame(() => {
        anchorFrame = requestAnimationFrame(() => {
          anchorFrame = null;
          if (disposed || !window.location.hash) return;
          const link = [...tabs.querySelectorAll('.vertical-tabs__menu-link')]
            .find(element => element.getAttribute('href') === window.location.hash);
          if (!link) return;
          const rect = link.getBoundingClientRect();
          const edge = Math.max(0, navigation.getBoundingClientRect().bottom,
            controls?.getBoundingClientRect().bottom ?? 0);
          if (rect.top < edge || rect.bottom > window.innerHeight) {
            window.scrollBy({top: rect.top - edge, behavior: 'instant'});
          }
        });
      });
    }

    function setSize(name, value) {
      const next = `${value}px`;
      if (tabs.style.getPropertyValue(name) !== next) tabs.style.setProperty(name, next);
    }

    function restore() {
      tabs.classList.remove('authors-scroll-ready');
      tabs.dataset.footerEdges = originalEdges;
      tabs.dataset.footerSpacedEdges = originalSpacedEdges;
      tabs.style.removeProperty('--authors-panel-reservation');
      tabs.style.removeProperty('--authors-panel-height');
      panel.removeAttribute('tabindex');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-labelledby');
    }

    function update() {
      frame = null;
      if (disposed) return;
      const headerHeight = Math.ceil(navigation.getBoundingClientRect().height
        + (controls?.getBoundingClientRect().height ?? 0));
      document.documentElement.style.setProperty('--sticky-navigation-height', `${headerHeight}px`);
      if (mobile.matches) {
        restore();
        return;
      }

      tabs.classList.add('authors-scroll-ready');
      // The scroll viewport is not an intrinsic content edge. Its own trailing
      // padding supplies clearance after the complete content, while the shared
      // footer behavior continues to measure the natural name-list edge.
      tabs.dataset.footerEdges = '.vertical-tabs__menu';
      tabs.dataset.footerSpacedEdges = '';
      panel.setAttribute('tabindex', '0');
      panel.setAttribute('role', 'region');
      const selected = panel.querySelector('.vertical-tabs__pane:not([hidden])');
      if (selected) {
        panel.setAttribute('aria-labelledby', selected.getAttribute('aria-labelledby'));
        if (active !== selected) panel.scrollTop = 0;
        active = selected;
      }

      const viewportSpace = Math.max(1, window.innerHeight - headerHeight);
      // Reserve a stable normal-flow height. Shrinking the viewport near the
      // footer must not move the footer itself and cause a measurement loop.
      setSize('--authors-panel-reservation', Math.min(panel.scrollHeight, viewportSpace));
      const top = Math.max(headerHeight, column.getBoundingClientRect().top);
      const bottom = Math.min(window.innerHeight, footer.getBoundingClientRect().top);
      setSize('--authors-panel-height', Math.max(1, bottom - top));
    }

    function schedule() {
      if (!disposed && frame === null) frame = requestAnimationFrame(update);
    }
    const resize = new ResizeObserver(schedule);
    [navigation, controls, tabs, column, panel, footer, ...panel.children].filter(Boolean).forEach(element => resize.observe(element));
    const mutation = new MutationObserver(schedule);
    mutation.observe(panel, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden']});
    window.addEventListener('scroll', schedule, {passive: true});
    window.addEventListener('resize', schedule, {passive: true});
    mobile.addEventListener('change', schedule);
    schedule();
    revealAnchor();
    window.addEventListener('pageshow', revealAnchor);
    window.addEventListener('hashchange', revealAnchor);
    cleanups.set(tabs, () => {
      disposed = true;
      resize.disconnect();
      mutation.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('pageshow', revealAnchor);
      window.removeEventListener('hashchange', revealAnchor);
      cancelAnimationFrame(anchorFrame);
      mobile.removeEventListener('change', schedule);
      if (frame !== null) cancelAnimationFrame(frame);
      restore();
    });
  }

  Drupal.behaviors.stickyNavigationOffset = {
    attach(context) {
      const roots = [...context.querySelectorAll(selector)];
      if (context.matches?.(selector)) roots.unshift(context);
      once('authors-scroll-panel', roots).forEach(install);
    },
    detach(context, settings, trigger) {
      if (trigger !== 'unload') return;
      const roots = [...context.querySelectorAll(selector)];
      if (context.matches?.(selector)) roots.unshift(context);
      once.remove('authors-scroll-panel', roots).forEach(tabs => {
        cleanups.get(tabs)?.();
        cleanups.delete(tabs);
      });
    },
  };
})(Drupal, once);
